
import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Stack,
    IconButton,
    Input,
    CircularProgress,
    FormControlLabel,
    Switch,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormHelperText
} from "@mui/material";
import { Upload, X, Plus, Trash2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import CustomEditor from "../../Common/CustomEditor";
import { createBlog, updateBlog, getAllBlogCategories, getBlogById } from "../../Services/BlogsManagement/BlogsApi";
import { useGlobalSnackbar } from "../../Components/Common/GlobalSanckbar";

const safeParseArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const AddEditBlogs = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showSnackbar } = useGlobalSnackbar();
    const editData = location.state?.blog;

    const [title, setTitle] = useState(editData?.title || "");
    const [description, setDescription] = useState(editData?.description || "");

    // Legacy support: if strings, wrap in one section. If parsed JSON array, use it.
    // However, given the requirement, we will start with one section or parse existing
    const [contentSections, setContentSections] = useState([{ id: Date.now(), content: "", imageFile: null }]);

    const [categoryId, setCategoryId] = useState(editData?.categoryId || "");
    const [type, setType] = useState(editData?.type || "blog");

    // SEO States
    const [heading, setHeading] = useState(editData?.heading || "");
    const [metaTitle, setMetaTitle] = useState(editData?.metaTitle || "");
    const [metaDescription, setMetaDescription] = useState(editData?.metaDescription || "");
    const [metaKeywords, setMetaKeywords] = useState(editData?.metaKeywords || "");
    const [metaOutlet, setMetaOutlet] = useState(safeParseArray(editData?.metaOutlet) || []);
    const [newMetaOutletInput, setNewMetaOutletInput] = useState("");
    const [canonicalUrl, setCanonicalUrl] = useState(editData?.canonicalUrl || "");
    const [isIndex, setIsIndex] = useState(
        editData?.isIndex !== undefined
            ? (editData.isIndex === "true" || editData.isIndex === true)
            : true
    );
    const [imageAltText, setImageAltText] = useState(editData?.imageAltText || "");

    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(editData?.coverImage || null);
    const [ogImage, setOgImage] = useState(null);
    const [ogImagePreview, setOgImagePreview] = useState(editData?.ogImage || null);
    const [ogImageError, setOgImageError] = useState('');

    const [isActive, setIsActive] = useState(editData?.isActive ?? true);

    const [isFeatured, setIsFeatured] = useState(editData?.isFeatured ?? false);

    const [categories, setCategories] = useState([]);

    const [loading, setLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    // Refs for smooth scroll to error fields
    const titleRef = useRef(null);
    const categoryRef = useRef(null);
    const typeRef = useRef(null);
    const descriptionRef = useRef(null);
    const coverImageRef = useRef(null);
    const sectionRefs = useRef([]);
    const headingRef = useRef(null);
    const metaTitleRef = useRef(null);
    const metaDescriptionRef = useRef(null);
    const metaKeywordsRef = useRef(null);
    const metaOutletRef = useRef(null);
    const canonicalUrlRef = useRef(null);
    const isIndexRef = useRef(null);

    const handleAddMetaOutletItem = () => {
        const trimmed = newMetaOutletInput.trim();
        if (!trimmed) return;
        setMetaOutlet(prev => [...prev, trimmed]);
        setNewMetaOutletInput('');
        if (fieldErrors.metaOutlet) {
            setFieldErrors(prev => ({ ...prev, metaOutlet: null }));
        }
    };

    const handleRemoveMetaOutletItem = (index) => {
        setMetaOutlet(prev => prev.filter((_, idx) => idx !== index));
    };

    // OG Image Handlers
    const handleOgImageChange = (e) => {
        const file = e.target.files && e.target.files[0];
        const inputElement = e.target;
        if (file) {
            if (!file.type.startsWith('image/')) {
                setOgImageError('Only image files are allowed.');
                setOgImage(null);
                setOgImagePreview(null);
                inputElement.value = "";
                return;
            }
            if (file.size > 2 * 1024 * 1024) {
                setOgImageError('Image size must be less than 2MB.');
                setOgImage(null);
                setOgImagePreview(null);
                inputElement.value = "";
                return;
            }
            setOgImage(file);
            setOgImageError('');
            const reader = new FileReader();
            reader.onloadend = () => {
                setOgImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveOgImage = () => {
        setOgImage(null);
        setOgImagePreview(null);
        setOgImageError('');
    };

    const scrollToRef = (ref) => {
        if (ref && ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await getAllBlogCategories();
                if (response && response.success && response.data) {
                    setCategories(response.data);
                }
            } catch (err) {
                console.error("Failed to load categories", err);
            }
        };
        fetchCategories();
    }, []);

    // Fetch full details if editing
    useEffect(() => {
        const blogId = editData?.id || editData?._id;
        if (blogId) {
            const fetchDetails = async () => {
                try {
                    const response = await getBlogById(blogId);
                    if (response && response.success && response.data) {
                        const data = response.data;
                        setTitle(data.title || "");
                        setDescription(data.description || "");

                        // Handle contents - it's already an array in the response
                        if (data.contents && Array.isArray(data.contents)) {
                            setContentSections(data.contents.map((item, idx) => ({
                                id: item.id || Date.now() + idx,
                                content: item.content || "",
                                imagePreview: item.image || null,
                                imageFile: null
                            })));
                        } else {
                            // Fallback for empty or invalid contents
                            setContentSections([{ id: Date.now(), content: "", imageFile: null }]);
                        }

                        setCategoryId(data.categoryId || "");
                        setType(data.type || "blog");
                        setHeading(data.heading || "");
                        setMetaTitle(data.metaTitle || "");
                        setMetaDescription(data.metaDescription || "");
                        setMetaKeywords(data.metaKeywords || "");
                        setMetaOutlet(safeParseArray(data.metaOutlet) || []);
                        setCanonicalUrl(data.canonicalUrl || "");
                        setIsIndex(
                            data.isIndex !== undefined
                                ? (data.isIndex === "true" || data.isIndex === true)
                                : true
                        );
                        setImageAltText(data.imageAltText || "");
                        setImagePreview(data.coverImage || null);
                        setOgImagePreview(data.ogImage || null);
                        setOgImage(null);
                        setOgImageError('');
                        setIsActive(data.isActive ?? true);
                        setIsFeatured(data.isFeatured ?? false);
                    }
                } catch (err) {
                    console.error("Error fetching blog details:", err);
                }
            };
            fetchDetails();
        }
    }, [editData]);


    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                setFieldErrors(prev => ({ ...prev, blogCoverImage: "Only image files (PNG, JPG, WEBP, SVG, GIF) are allowed" }));
                showSnackbar("Only image files are allowed", "error");
                e.target.value = "";
                return;
            }

            // if (file.size > 5 * 1024 * 1024) {
            //     setFieldErrors(prev => ({ ...prev, blogCoverImage: "Image size must be less than 5MB" }));
            //     showSnackbar("Image size must be less than 5MB", "error");
            //     e.target.value = "";
            //     return;
            // }

            setImage(file);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);

            // Clear image error when user selects an image
            if (fieldErrors.blogCoverImage) {
                setFieldErrors(prev => ({ ...prev, blogCoverImage: null }));
            }
        }
    };

    const handleRemoveImage = () => {
        setImage(null);
        setImagePreview(null);
    };

    // --- Dynamic Content Sections Logic ---

    const handleAddSection = () => {
        setContentSections([
            ...contentSections,
            { id: Date.now(), content: "", imageFile: null }
        ]);
    };

    const handleRemoveSection = (index) => {
        const updated = contentSections.filter((_, i) => i !== index);
        setContentSections(updated);
    };

    const handleSectionContentChange = (value, index) => {
        const updated = [...contentSections];
        updated[index].content = value;
        setContentSections(updated);
    };

    const handleSectionImageChange = (e, index) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                setFieldErrors(prev => ({ ...prev, contentImages: "Only image files (PNG, JPG, WEBP, SVG, GIF) are allowed" }));
                showSnackbar("Only image files are allowed for section images", "error");
                e.target.value = "";
                return;
            }

            // if (file.size > 5 * 1024 * 1024) {
            //     setFieldErrors(prev => ({ ...prev, contentImages: "Section image size must be less than 5MB" }));
            //     showSnackbar("Section image size must be less than 5MB", "error");
            //     e.target.value = "";
            //     return;
            // }

            const updated = [...contentSections];
            updated[index].imageFile = file;
            const reader = new FileReader();
            reader.onloadend = () => {
                updated[index].imagePreview = reader.result;
                setContentSections(updated);
            };
            reader.readAsDataURL(file);
            if (fieldErrors.contentImages) {
                setFieldErrors(prev => ({ ...prev, contentImages: null }));
            }
        }
    };

    // --------------------------------------

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (image && !image.type.startsWith("image/")) {
            setFieldErrors(prev => ({ ...prev, blogCoverImage: "Only image files are allowed" }));
            showSnackbar("Only image files are allowed for cover image", "error");
            return;
        }

        const invalidSectionImage = contentSections.find(s => s.imageFile && !s.imageFile.type.startsWith("image/"));
        if (invalidSectionImage) {
            setFieldErrors(prev => ({ ...prev, contentImages: "Only image files are allowed for section images" }));
            showSnackbar("Only image files are allowed for section images", "error");
            return;
        }

        setLoading(true);
        setFieldErrors({});

        const formData = new FormData();
        formData.append("title", title);
        formData.append("description", description);
        formData.append("categoryId", categoryId);
        formData.append("type", type);

        // Prepare contents structure
        // Include existing image URLs when no new image is uploaded
        const contentsPayload = contentSections.map(section => ({
            content: section.content,
            image: section.imagePreview || null // Include existing image URL if available
        }));

        formData.append("contents", JSON.stringify(contentsPayload));
        formData.append("heading", heading || "");
        formData.append("metaTitle", metaTitle || "");
        formData.append("metaDescription", metaDescription || "");
        formData.append("metaKeywords", metaKeywords || "");
        formData.append("metaOutlet", JSON.stringify(metaOutlet || []));
        formData.append("canonicalUrl", canonicalUrl || "");
        formData.append("isIndex", isIndex ? "true" : "false");
        formData.append("imageAltText", imageAltText || "");
        formData.append("isActive", isActive);
        formData.append("isFeatured", isFeatured);

        if (image) {
            formData.append("blogCoverImage", image);
        }

        // Add OG Image if present
        if (ogImage) {
            formData.append("ogImage", ogImage);
        }

        contentSections.forEach((section) => {
            if (section.imageFile) {
                formData.append("contentImages", section.imageFile);
            }
        });

        try {
            if (editData) {
                const blogId = editData.id || editData._id;
                await updateBlog(blogId, formData);
                showSnackbar("Blog updated successfully!", "success");
            } else {
                await createBlog(formData);
                showSnackbar("Blog created successfully!", "success");
            }

            setTimeout(() => navigate("/blogs-management"), 1500);
        } catch (err) {
            const responseData = err.response?.data;
            showSnackbar(responseData?.message || "Failed to save blog.", "error");

            // Map field-level errors if they exist in the response
            if (responseData?.details && Array.isArray(responseData.details)) {
                const errorsMap = {};
                let firstErrorField = null;
                let contentImagesErrorIndex = -1;

                responseData.details.forEach(item => {
                    let field = item.field;
                    if (field === "general" && item.message.toLowerCase().includes("cover image")) {
                        field = "blogCoverImage";
                    }
                    errorsMap[field] = item.message;
                    if (!firstErrorField) firstErrorField = field;

                    // For contentImages errors, find which section by matching filename
                    if (field === "contentImages") {
                        // Try to match the filename in the error message to the section
                        const match = item.message.match(/"([^"]+)"/);
                        const errorFileName = match ? match[1] : null;
                        if (errorFileName && contentImagesErrorIndex === -1) {
                            const idx = contentSections.findIndex(s =>
                                s.imageFile && s.imageFile.name === errorFileName
                            );
                            contentImagesErrorIndex = idx !== -1 ? idx : 0;
                        } else if (contentImagesErrorIndex === -1) {
                            contentImagesErrorIndex = 0;
                        }
                    }
                });

                setFieldErrors(errorsMap);

                // Smooth scroll to the first errored field
                setTimeout(() => {
                    if (firstErrorField === "title") scrollToRef(titleRef);
                    else if (firstErrorField === "categoryId") scrollToRef(categoryRef);
                    else if (firstErrorField === "type") scrollToRef(typeRef);
                    else if (firstErrorField === "description") scrollToRef(descriptionRef);
                    else if (firstErrorField === "blogCoverImage") scrollToRef(coverImageRef);
                    else if (firstErrorField === "heading") scrollToRef(headingRef);
                    else if (firstErrorField === "metaTitle") scrollToRef(metaTitleRef);
                    else if (firstErrorField === "metaDescription") scrollToRef(metaDescriptionRef);
                    else if (firstErrorField === "metaKeywords") scrollToRef(metaKeywordsRef);
                    else if (firstErrorField === "metaOutlet") scrollToRef(metaOutletRef);
                    else if (firstErrorField === "canonicalUrl") scrollToRef(canonicalUrlRef);
                    else if (firstErrorField === "isIndex") scrollToRef(isIndexRef);
                    else if (firstErrorField === "contentImages" && contentImagesErrorIndex !== -1) {
                        const sectionRef = sectionRefs.current[contentImagesErrorIndex];
                        if (sectionRef) sectionRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 50);

            } else if (responseData?.message?.toLowerCase().includes("cover image")) {
                // Manual mapping for specific message if details is null
                setFieldErrors(prev => ({ ...prev, blogCoverImage: responseData.message }));
                setTimeout(() => scrollToRef(coverImageRef), 50);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: "#FBFBFB", minHeight: "100vh" }}>




            <Typography sx={{ fontSize: "1.8rem", fontWeight: 500, mb: 4 }}>
                {editData ? "Edit Blog" : "Add New Blog"}
            </Typography>

            <Paper sx={{ p: 4, borderRadius: 3, border: "1px solid #E8E8E8", maxWidth: 900, mx: "auto", boxShadow: "none" }}>
                {/* {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>} */}
                {/* {success && <Alert severity="success" sx={{ mb: 3 }}>Saved successfully! Redirecting...</Alert>} */}

                <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>

                        {/* Title Field */}
                        <Box ref={titleRef}>
                            <Typography sx={{ mb: 1, fontWeight: 500 }}>
                                Title
                            </Typography>
                            <TextField type="text" size="small" fullWidth
                                placeholder="Enter blog title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                error={!!fieldErrors.title}
                                helperText={fieldErrors.title}
                            />
                        </Box>

                        {/* Category & Type */}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Box ref={categoryRef} sx={{ flex: 1 }}>
                                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                                    Category
                                </Typography>
                                <FormControl fullWidth size="small" error={!!fieldErrors.categoryId}>
                                    <InputLabel>Select Category</InputLabel>
                                    <Select
                                        label="Select Category"
                                        value={categoryId}
                                        onChange={(e) => setCategoryId(e.target.value)}
                                    >
                                        {categories.map((cat) => (
                                            <MenuItem key={cat.id} value={cat.id}>
                                                {cat.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {fieldErrors.categoryId && <FormHelperText>{fieldErrors.categoryId}</FormHelperText>}
                                </FormControl>
                            </Box>

                            <Box ref={typeRef} sx={{ flex: 1 }}>
                                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                                    Type
                                </Typography>
                                <FormControl fullWidth size="small" error={!!fieldErrors.type}>
                                    <InputLabel>Select Type</InputLabel>
                                    <Select
                                        label="Select Type"
                                        value={type}
                                        onChange={(e) => setType(e.target.value)}
                                    >
                                        <MenuItem value="blog">Blog</MenuItem>
                                        <MenuItem value="guide">Guide</MenuItem>
                                    </Select>
                                    {fieldErrors.type && <FormHelperText>{fieldErrors.type}</FormHelperText>}
                                </FormControl>
                            </Box>
                        </Stack>

                        {/* Description */}
                        <Box ref={descriptionRef}>
                            <Typography sx={{ mb: 1, fontWeight: 500 }}>
                                Short Description
                            </Typography>
                            <TextField
                                multiline
                                rows={3}
                                fullWidth
                                placeholder="Enter short description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                error={!!fieldErrors.description}
                                helperText={fieldErrors.description}
                            />
                        </Box>

                        <Box component="hr" sx={{ my: 1, border: "none", borderTop: "1px solid #eee" }} />

                        {/* Dynamic Content Sections */}
                        <Box>
                            <Typography sx={{ mb: 2, fontWeight: 600, fontSize: "1.1rem" }}>
                                Article Sections
                            </Typography>

                            {contentSections.map((section, index) => (
                                <Box
                                    key={section.id}
                                    ref={el => sectionRefs.current[index] = el}
                                    sx={{
                                        mb: 4, p: 3,
                                        border: fieldErrors.contentImages && contentSections[index]?.imageFile
                                            ? '1.5px solid #d32f2f'
                                            : '1px solid #eee',
                                        borderRadius: 2,
                                        bgcolor: '#FAFAFA',
                                        transition: 'border-color 0.2s'
                                    }}
                                >
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                        <Typography sx={{ fontWeight: 500 }}>
                                            Section {index + 1}
                                        </Typography>
                                        {contentSections.length > 1 && (
                                            <IconButton onClick={() => handleRemoveSection(index)} size="small" color="error">
                                                <Trash2 size={18} />
                                            </IconButton>
                                        )}
                                    </Stack>

                                    <Typography sx={{ mb: 1, fontSize: "0.9rem", color: "#555" }}>
                                        Content (Full Article)
                                    </Typography>
                                    <CustomEditor
                                        value={section.content}
                                        onChange={(val) => handleSectionContentChange(val, index)}
                                        placeholder="Write article content..."
                                        minHeight={200}
                                        maxHeight={350}
                                    />

                                    <Box sx={{ mt: 2 }}>
                                        <Typography sx={{ mb: 1, fontSize: "0.9rem", color: "#555" }}>
                                            Additional Content Image (Optional)
                                        </Typography>

                                        {!section.imagePreview ? (
                                            <Input
                                                type="file"
                                                inputProps={{ accept: "image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml" }}
                                                onChange={(e) => handleSectionImageChange(e, index)}
                                                fullWidth
                                                disableUnderline
                                                sx={{
                                                    border: fieldErrors.contentImages && contentSections[index]?.imageFile
                                                        ? '1px solid #d32f2f'
                                                        : '1px solid #ddd',
                                                    borderRadius: 1, p: 1, bgcolor: '#fff'
                                                }}
                                            />
                                        ) : (
                                            <Box sx={{ mt: 1, position: "relative", width: "fit-content" }}>
                                                <img
                                                    src={section.imagePreview}
                                                    alt={`Section ${index + 1}`}
                                                    style={{ maxWidth: "100%", maxHeight: 150, borderRadius: 8 }}
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        const updated = [...contentSections];
                                                        updated[index].imageFile = null;
                                                        updated[index].imagePreview = null;
                                                        setContentSections(updated);
                                                        // Clear the contentImages error when user removes/changes image
                                                        setFieldErrors(prev => ({ ...prev, contentImages: null }));
                                                    }}
                                                    sx={{ position: "absolute", top: -8, right: -8, bgcolor: "white", boxShadow: 1 }}
                                                >
                                                    <X size={14} />
                                                </IconButton>
                                            </Box>
                                        )}

                                        {/* Show error inline under the image input of the errored section */}
                                        {fieldErrors.contentImages && contentSections[index]?.imageFile && (
                                            <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mt: 0.5 }}>
                                                {fieldErrors.contentImages}
                                            </Typography>
                                        )}

                                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                                            Upload an image related to this content section.
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}

                            <Button
                                variant="outlined"
                                startIcon={<Plus size={18} />}
                                onClick={handleAddSection}
                                sx={{ borderRadius: 2, textTransform: "none", borderStyle: "dashed", borderWidth: 2 }}
                                fullWidth
                            >
                                Add Section
                            </Button>
                        </Box>

                        {/* Image Upload */}
                        <Box ref={coverImageRef}>
                            <Typography sx={{ mb: 1, fontWeight: 500 }}>
                                Blog Cover Image
                            </Typography>

                            {!imagePreview ? (
                                <Box component="label"
                                    sx={{
                                        display: "block",
                                        border: fieldErrors.blogCoverImage ? '2px dashed #d32f2f' : '2px dashed #E0E0E0',
                                        borderRadius: 2,
                                        p: 4,
                                        textAlign: "center",
                                        cursor: "pointer",
                                        transition: 'border-color 0.2s'
                                    }}
                                >
                                    <Input
                                        type="file"
                                        inputProps={{ accept: "image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml" }}
                                        sx={{ display: "none" }}
                                        onChange={handleImageChange}
                                    />
                                    <Upload size={32} color={fieldErrors.blogCoverImage ? '#d32f2f' : '#888'} />
                                    <Typography color={fieldErrors.blogCoverImage ? 'error' : 'textSecondary'} sx={{ mt: 1 }}>
                                        Click to upload cover image
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ position: "relative", width: "fit-content" }}>
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 12, display: "block" }}
                                    />
                                    <IconButton
                                        onClick={handleRemoveImage}
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            bgcolor: "rgba(255,255,255,0.8)",
                                        }}
                                    >
                                        <X size={16} />
                                    </IconButton>
                                </Box>
                            )}
                            {fieldErrors.blogCoverImage && (
                                <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, display: "block" }}>
                                    {fieldErrors.blogCoverImage}
                                </Typography>
                            )}
                        </Box>

                        <Box component="hr" sx={{ my: 2, border: "none", borderTop: "1px solid #eee" }} />

                        <Box sx={{ pt: 1 }}>
                            <Typography sx={{ fontSize: "1.1rem", fontWeight: 600, color: '#1E293B', mb: 0.5 }}>
                                SEO Metadata
                            </Typography>
                            <Typography sx={{ color: '#64748B', fontSize: '0.8rem', mb: 2.5 }}>
                                Configure search engine optimization metadata for this blog post.
                            </Typography>

                            <Stack spacing={2.5}>
                                {/* Heading */}
                                <Box ref={headingRef}>
                                    <Typography sx={{ mb: 1, fontWeight: 500, fontSize: "0.85rem", color: "#334155" }}>
                                        Heading
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Enter heading"
                                        value={heading}
                                        onChange={(e) => {
                                            setHeading(e.target.value);
                                            if (fieldErrors.heading) {
                                                setFieldErrors(prev => ({ ...prev, heading: null }));
                                            }
                                        }}
                                        error={Boolean(fieldErrors.heading)}
                                        helperText={fieldErrors.heading}
                                    />
                                </Box>

                                {/* Meta Title */}
                                <Box ref={metaTitleRef}>
                                    <Typography sx={{ mb: 1, fontWeight: 500, fontSize: "0.85rem", color: "#334155" }}>
                                        Meta Title
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Enter meta title"
                                        value={metaTitle}
                                        onChange={(e) => {
                                            setMetaTitle(e.target.value);
                                            if (fieldErrors.metaTitle) {
                                                setFieldErrors(prev => ({ ...prev, metaTitle: null }));
                                            }
                                        }}
                                        error={Boolean(fieldErrors.metaTitle)}
                                        helperText={fieldErrors.metaTitle}
                                    />
                                </Box>

                                {/* Meta Description */}
                                <Box ref={metaDescriptionRef}>
                                    <Typography sx={{ mb: 1, fontWeight: 500, fontSize: "0.85rem", color: "#334155" }}>
                                        Meta Description
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        size="small"
                                        placeholder="Enter meta description"
                                        value={metaDescription}
                                        onChange={(e) => {
                                            setMetaDescription(e.target.value);
                                            if (fieldErrors.metaDescription) {
                                                setFieldErrors(prev => ({ ...prev, metaDescription: null }));
                                            }
                                        }}
                                        error={Boolean(fieldErrors.metaDescription)}
                                        helperText={fieldErrors.metaDescription}
                                    />
                                </Box>

                                {/* Meta Keywords */}
                                <Box ref={metaKeywordsRef}>
                                    <Typography sx={{ mb: 1, fontWeight: 500, fontSize: "0.85rem", color: "#334155" }}>
                                        Meta Keywords
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Enter meta keywords"
                                        value={metaKeywords}
                                        onChange={(e) => {
                                            setMetaKeywords(e.target.value);
                                            if (fieldErrors.metaKeywords) {
                                                setFieldErrors(prev => ({ ...prev, metaKeywords: null }));
                                            }
                                        }}
                                        error={Boolean(fieldErrors.metaKeywords)}
                                        helperText={fieldErrors.metaKeywords || "Comma separated keywords"}
                                    />
                                </Box>

                                {/* Meta Outlet */}
                                <Box ref={metaOutletRef}>
                                    <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: "0.85rem", color: "#334155" }}>
                                        Meta Outlet (H2 Outlet)
                                    </Typography>
                                    <Typography sx={{ color: '#64748B', fontSize: '0.8rem', mb: 1.5 }}>
                                        Add meta outlet bullet points for this blog.
                                    </Typography>

                                    <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                                        <TextField
                                            size="small"
                                            placeholder="Add meta outlet item..."
                                            value={newMetaOutletInput}
                                            onChange={(e) => setNewMetaOutletInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddMetaOutletItem();
                                                }
                                            }}
                                            fullWidth
                                        />
                                        <Button
                                            variant="contained"
                                            startIcon={<Plus size={16} />}
                                            onClick={handleAddMetaOutletItem}
                                            sx={{
                                                backgroundColor: '#1E293B',
                                                color: '#FFFFFF',
                                                textTransform: 'none',
                                                boxShadow: 'none',
                                                borderRadius: '6px',
                                                fontWeight: 500,
                                                px: 2.5,
                                                whiteSpace: 'nowrap',
                                                '&:hover': { backgroundColor: '#334155', boxShadow: 'none' }
                                            }}
                                        >
                                            Add
                                        </Button>
                                    </Stack>

                                    {metaOutlet.length > 0 && (
                                        <Stack spacing={1}>
                                            {metaOutlet.map((item, idx) => (
                                                <Box
                                                    key={idx}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        p: 1.25,
                                                        backgroundColor: '#F8FAFC',
                                                        border: '1px solid #E2E8F0',
                                                        borderRadius: '6px'
                                                    }}
                                                >
                                                    <Typography sx={{ color: '#334155', fontSize: '0.825rem', fontWeight: 500 }}>
                                                        • {item}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemoveMetaOutletItem(idx)}
                                                        sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: '#EF4444', backgroundColor: '#FEE2E2' } }}
                                                    >
                                                        <Trash2 size={16} />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Stack>
                                    )}
                                    {fieldErrors.metaOutlet && (
                                        <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, display: "block" }}>
                                            {fieldErrors.metaOutlet}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Canonical URL */}
                                <Box ref={canonicalUrlRef}>
                                    <Typography sx={{ mb: 1, fontWeight: 500, fontSize: "0.85rem", color: "#334155" }}>
                                        Canonical URL
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Enter canonical URL"
                                        value={canonicalUrl}
                                        onChange={(e) => {
                                            setCanonicalUrl(e.target.value);
                                            if (fieldErrors.canonicalUrl) {
                                                setFieldErrors(prev => ({ ...prev, canonicalUrl: null }));
                                            }
                                        }}
                                        error={Boolean(fieldErrors.canonicalUrl)}
                                        helperText={fieldErrors.canonicalUrl}
                                    />
                                </Box>

                                {/* Is Index */}
                                <Box ref={isIndexRef}>
                                    <Typography sx={{ mb: 1, fontWeight: 500, fontSize: "0.85rem", color: "#334155" }}>
                                        Is Index
                                    </Typography>
                                    <Box
                                        sx={{
                                            border: '1px solid #E2E8F0',
                                            borderRadius: '6px',
                                            boxShadow: 'none',
                                            p: 1.5,
                                            bgcolor: '#FFFFFF'
                                        }}
                                    >
                                        <Stack direction="row" spacing={3} alignItems="center">
                                            <Box
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}
                                                onClick={() => {
                                                    setIsIndex(true);
                                                    if (fieldErrors.isIndex) setFieldErrors(prev => ({ ...prev, isIndex: null }));
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    name="isIndex"
                                                    checked={isIndex === true}
                                                    onChange={() => {
                                                        setIsIndex(true);
                                                        if (fieldErrors.isIndex) {
                                                            setFieldErrors(prev => ({ ...prev, isIndex: null }));
                                                        }
                                                    }}
                                                    style={{ cursor: 'pointer', margin: 0 }}
                                                />
                                                <Typography sx={{ fontSize: '0.85rem', color: '#334155', cursor: 'pointer' }}>True (Indexable)</Typography>
                                            </Box>
                                            <Box
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}
                                                onClick={() => {
                                                    setIsIndex(false);
                                                    if (fieldErrors.isIndex) setFieldErrors(prev => ({ ...prev, isIndex: null }));
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    name="isIndex"
                                                    checked={isIndex === false}
                                                    onChange={() => {
                                                        setIsIndex(false);
                                                        if (fieldErrors.isIndex) {
                                                            setFieldErrors(prev => ({ ...prev, isIndex: null }));
                                                        }
                                                    }}
                                                    style={{ cursor: 'pointer', margin: 0 }}
                                                />
                                                <Typography sx={{ fontSize: '0.85rem', color: '#334155', cursor: 'pointer' }}>False (No Index)</Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                    {fieldErrors.isIndex && (
                                        <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, display: "block" }}>
                                            {fieldErrors.isIndex}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Image Alt Text */}
                                <Box>
                                    <Typography sx={{ mb: 1, fontWeight: 500, fontSize: "0.85rem", color: "#334155" }}>
                                        Image Alt Text
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Enter image alt text"
                                        value={imageAltText}
                                        onChange={(e) => setImageAltText(e.target.value)}
                                        error={Boolean(fieldErrors.imageAltText)}
                                        helperText={fieldErrors.imageAltText}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.85rem',
                                                bgcolor: '#FFFFFF',
                                                '& fieldset': { borderColor: '#E0E0E0' },
                                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                            }
                                        }}
                                    />
                                </Box>

                                {/* OG Image */}
                                <Box>
                                    <Typography sx={{ mb: 1, fontWeight: 500, fontSize: "0.85rem", color: "#334155" }}>
                                        OG Image
                                    </Typography>
                                    {!ogImagePreview ? (
                                        <Box
                                            component="label"
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                border: ogImageError ? "1px dashed #EF4444" : "1px dashed #CBD5E1",
                                                borderRadius: "6px",
                                                height: 140,
                                                cursor: loading ? "not-allowed" : "pointer",
                                                bgcolor: "#F8FAFC",
                                                transition: "all 0.2s ease-in-out",
                                                "&:hover": {
                                                    borderColor: ogImageError ? "#EF4444" : "#6E8964",
                                                    bgcolor: "#F1F5F9"
                                                }
                                            }}
                                        >
                                            <input
                                                type="file"
                                                accept="image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml, image/*"
                                                style={{ display: "none" }}
                                                disabled={loading}
                                                onChange={handleOgImageChange}
                                            />
                                            <Upload size={22} color={ogImageError ? "#EF4444" : "#64748B"} style={{ marginBottom: 6 }} />
                                            <Typography sx={{ color: ogImageError ? "#EF4444" : "#475569", fontSize: '0.8rem', fontWeight: 500 }}>
                                                Click to upload OG image
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: ogImageError ? "#EF4444" : "#94A3B8", fontSize: '0.7rem' }}>
                                                PNG, JPG or WebP (Max 2MB)
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ position: "relative", width: "100%", height: 140, bgcolor: "#F8FAFC", borderRadius: "6px", border: "1px solid #E2E8F0", display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                            <Box
                                                component="img"
                                                src={ogImagePreview}
                                                alt="OG Image Preview"
                                                sx={{
                                                    maxWidth: "100%",
                                                    maxHeight: "100%",
                                                    objectFit: "contain",
                                                }}
                                            />
                                            <IconButton
                                                onClick={handleRemoveOgImage}
                                                disabled={loading}
                                                sx={{
                                                    position: "absolute",
                                                    top: 6,
                                                    right: 6,
                                                    backgroundColor: "#FFFFFF",
                                                    border: "1px solid #E2E8F0",
                                                    color: "#EF4444",
                                                    p: 0.5,
                                                    boxShadow: 'none',
                                                    '&:hover': { bgcolor: '#FEE2E2' }
                                                }}
                                                size="small"
                                            >
                                                <X size={14} />
                                            </IconButton>
                                        </Box>
                                    )}
                                    {ogImageError && (
                                        <Typography sx={{ color: '#EF4444', fontSize: '0.75rem', mt: 0.75, fontWeight: 400 }}>
                                            {ogImageError}
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                        </Box>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                />
                            }
                            label="Active Status"
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isFeatured}
                                    onChange={(e) => setIsFeatured(e.target.checked)}
                                />
                            }
                            label="Featured Status"
                        />

                        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => navigate("/blogs-management")}
                                sx={{
                                    borderRadius: '6px',
                                    fontWeight: 500,
                                    borderColor: '#E2E8F0',
                                    color: '#475569',
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC', boxShadow: 'none' }
                                }}
                            >
                                Cancel
                            </Button>

                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                sx={{
                                    bgcolor: "#FFBD65",
                                    color: "#000000",
                                    borderRadius: '6px',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    '&:hover': { bgcolor: "#f5b35b", boxShadow: 'none' }
                                }}
                            >
                                {loading
                                    ? <CircularProgress size={20} />
                                    : editData
                                        ? "Update Blog"
                                        : "Create Blog"}
                            </Button>
                        </Stack>

                    </Stack>
                </form>
            </Paper>
        </Box>
    );
};

export default AddEditBlogs;
